/**
 * Platform admin dashboard — Applox internal ops only.
 *
 * Visibility is enforced server-side (PLATFORM_ADMIN_EMAILS allowlist
 * via requirePlatformAdmin); this page just gates the navigation. If
 * a non-admin somehow lands here, every fetch returns 403 and we show
 * an explainer instead of a half-rendered page.
 *
 * Four tabs reflect the views ops actually uses:
 *   • Tenants — fleet-wide table with member/server/cost summaries
 *   • Servers — every live Hetzner server across the fleet
 *   • Signups — the most recent users (catch onboarding spikes / abuse)
 *   • Drift — hetzner_reconciliation_runs, so console-side deletions surface
 */
import { Fragment, useEffect, useState, type ReactNode } from 'react'
import {
  api,
  type ApiError,
  type AdminTenantDto,
  type AdminServerDto,
  type AdminSignupDto,
  type AdminDriftDto,
} from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { AppShell } from '../components/shell/AppShell'
import { PageHeader } from '../components/shell/PageHeader'
import { SegmentedTabs } from '../components/ui/segmented-tabs'
import { Badge, StatusBox } from '../components/ui/badge'
import { Card, CardContent } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'

type Tab = 'tenants' | 'servers' | 'signups' | 'drift'

export function AdminPage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const [tab, setTab] = useState<Tab>('tenants')

  if (!accessToken) {
    navigate('/login')
    return null
  }

  return (
    <AppShell crumbs={[{ label: 'Admin' }]}>
      <PageHeader
        eyebrow="Platform"
        title={
          <>
            Admin
            <Badge variant="warning">Internal</Badge>
          </>
        }
        description="Fleet view across every tenant: members, servers, spend and infrastructure drift."
        actions={
          <SegmentedTabs<Tab>
            value={tab}
            onChange={setTab}
            items={[
              { value: 'tenants', label: 'Tenants' },
              { value: 'servers', label: 'Servers' },
              { value: 'signups', label: 'Signups' },
              { value: 'drift', label: 'Drift' },
            ]}
          />
        }
      />

      {tab === 'tenants' && <TenantsTab accessToken={accessToken} />}
      {tab === 'servers' && <ServersTab accessToken={accessToken} />}
      {tab === 'signups' && <SignupsTab accessToken={accessToken} />}
      {tab === 'drift' && <DriftTab accessToken={accessToken} />}
    </AppShell>
  )
}

/* ── Table primitives (doWallet table DNA) ─────────────────────────── */

function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-foreground/10">
      <table className="w-full text-left text-sm">{children}</table>
    </div>
  )
}
function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <th className={'h-10 px-2 text-xs font-medium text-muted-foreground first:pl-3 last:pr-3 ' + (right ? 'text-right' : '')}>{children}</th>
}
function Td({ children, right, className = '' }: { children: ReactNode; right?: boolean; className?: string }) {
  return <td className={'p-2 align-top first:pl-3 last:pr-3 ' + (right ? 'text-right tabular-nums ' : '') + className}>{children}</td>
}

function TenantsTab({ accessToken }: { accessToken: string }) {
  const data = useFetched(() => api.admin.listTenants(accessToken).then((r) => r.tenants))
  if (data.kind === 'loading') return <Loading />
  if (data.kind === 'error') return <ErrorBox error={data.error} />
  const totals = data.value.reduce(
    (acc, t) => ({
      members: acc.members + t.memberCount,
      servers: acc.servers + t.serverCount,
      monthlyEur: acc.monthlyEur + t.monthlyCostEur,
      anthropic30d: acc.anthropic30d + t.anthropicLast30dUsd,
    }),
    { members: 0, servers: 0, monthlyEur: 0, anthropic30d: 0 },
  )
  return (
    <>
      <FleetSummary
        tenants={data.value.length}
        members={totals.members}
        servers={totals.servers}
        monthlyEur={totals.monthlyEur}
        anthropic30d={totals.anthropic30d}
      />
      <Table>
        <thead className="bg-muted/30">
          <tr>
            <Th>Tenant</Th>
            <Th>Status</Th>
            <Th right>Members</Th>
            <Th right>Servers</Th>
            <Th right>€/mo</Th>
            <Th right>Anthropic 30d</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {data.value.map((t: AdminTenantDto) => (
            <tr key={t.id} className="border-b last:border-b-0 hover:bg-muted/50">
              <Td>
                <div className="font-medium text-foreground">{t.name}</div>
                <div className="font-mono text-xs text-muted-foreground">{t.slug}</div>
              </Td>
              <Td>
                <Badge variant={t.status === 'active' ? 'success' : 'outline'}>{t.status}</Badge>
              </Td>
              <Td right>{t.memberCount}</Td>
              <Td right>{t.serverCount}</Td>
              <Td right>€{t.monthlyCostEur.toFixed(2)}</Td>
              <Td right>${t.anthropicLast30dUsd.toFixed(2)}</Td>
              <Td className="text-xs text-muted-foreground">
                {new Date(t.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  )
}

function ServersTab({ accessToken }: { accessToken: string }) {
  const data = useFetched(() => api.admin.listServers(accessToken).then((r) => r.servers))
  if (data.kind === 'loading') return <Loading />
  if (data.kind === 'error') return <ErrorBox error={data.error} />
  if (data.value.length === 0) {
    return <EmptyState title="No servers in the fleet" body="Servers appear here once a tenant connects Hetzner and creates a project or adds a domain." />
  }
  return (
    <Table>
      <thead className="bg-muted/30">
        <tr>
          <Th>Server</Th>
          <Th>Tenant</Th>
          <Th>Role</Th>
          <Th>Domain</Th>
          <Th>IP</Th>
          <Th>Type</Th>
          <Th>Status</Th>
          <Th right>€/mo</Th>
        </tr>
      </thead>
      <tbody>
        {data.value.map((s: AdminServerDto) => (
          <tr key={s.id} className="border-b last:border-b-0 hover:bg-muted/50">
            <Td className="font-mono text-xs text-muted-foreground">
              {s.hetznerName}
              <div className="text-[10px]">#{s.hetznerServerId}</div>
            </Td>
            <Td className="font-mono text-xs">{s.tenantSlug}</Td>
            <Td><Badge variant="secondary">{s.role}</Badge></Td>
            <Td className="text-xs">{s.boundDomain ?? '—'}</Td>
            <Td className="font-mono text-xs">{s.publicIpv4 ?? '—'}</Td>
            <Td className="font-mono text-xs">{s.serverType}</Td>
            <Td><Badge variant={s.status === 'running' ? 'success' : 'outline'}>{s.status}</Badge></Td>
            <Td right>€{s.monthlyCostEur.toFixed(2)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

function SignupsTab({ accessToken }: { accessToken: string }) {
  const data = useFetched(() => api.admin.listSignups(accessToken, 100).then((r) => r.signups))
  if (data.kind === 'loading') return <Loading />
  if (data.kind === 'error') return <ErrorBox error={data.error} />
  return (
    <Table>
      <thead className="bg-muted/30">
        <tr>
          <Th>User</Th>
          <Th>Tenant</Th>
          <Th>Role</Th>
          <Th>Signed up</Th>
        </tr>
      </thead>
      <tbody>
        {data.value.map((s: AdminSignupDto) => (
          <tr key={s.userId} className="border-b last:border-b-0 hover:bg-muted/50">
            <Td>
              <div className="text-foreground">{s.name ?? s.email}</div>
              {s.name && <div className="text-xs text-muted-foreground">{s.email}</div>}
            </Td>
            <Td className="font-mono text-xs">{s.tenantSlug ?? '(none)'}</Td>
            <Td>{s.role ? <Badge variant="secondary">{s.role}</Badge> : <span className="text-muted-foreground">—</span>}</Td>
            <Td className="text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleString()}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

/**
 * Drift tab — surfaces hetzner_reconciliation_runs rows so ops can
 * see "tenant X manually deleted their server in the Hetzner
 * console" within an hour of it happening, instead of months later
 * when the bill arrives.
 *
 * Default view: drift-only (clean runs are noise). Toggle to "all"
 * when ops wants to confirm the cron is actually running. The JSON
 * details column expands inline — no modal — so an operator can
 * scan multiple rows quickly.
 */
function DriftTab({ accessToken }: { accessToken: string }) {
  const [onlyDrift, setOnlyDrift] = useState(true)
  const [data, setData] = useState<AdminDriftDto[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    api.admin
      .listDrift(accessToken, { onlyDrift, limit: 200 })
      .then((res) => {
        if (!cancelled) setData(res.runs)
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, onlyDrift])

  if (error) return <ErrorBox error={error} />
  if (data === null) return <Loading />

  // Quick "last 24h" headline so ops sees the trend without
  // counting rows. We base it on what the server returned (200
  // most recent), which is enough for any realistic drift volume.
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000
  const last24hDrift = data.filter(
    (r) => r.driftDetected && new Date(r.ranAt).getTime() >= dayAgo,
  ).length

  return (
    <>
      <Card size="sm">
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-foreground">
              <span className="font-semibold tabular-nums">{last24hDrift}</span> drift event
              {last24hDrift === 1 ? '' : 's'} in the last 24 hours
            </p>
            <p className="text-xs text-muted-foreground">
              Reconcile cron runs hourly via systemd timer — see
              infra/scripts/reconcile-hetzner.sh.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={onlyDrift}
              onChange={(e) => setOnlyDrift(e.target.checked)}
              className="size-4 rounded-sm"
            />
            Only show drift events
          </label>
        </CardContent>
      </Card>

      {data.length === 0 ? (
        <EmptyState
          title={onlyDrift ? 'No drift events' : 'No reconciliation runs yet'}
          body={
            onlyDrift
              ? 'The fleet matches Hetzner.'
              : 'The cron may not be wired up yet — check the reconcile systemd timer on the control plane.'
          }
        />
      ) : (
        <Table>
          <thead className="bg-muted/30">
            <tr>
              <Th>When</Th>
              <Th>Tenant</Th>
              <Th>Doable / Hetzner</Th>
              <Th>Drift</Th>
              <Th right>Duration</Th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => {
              const isExpanded = expanded === r.id
              return (
                <Fragment key={r.id}>
                  <tr
                    className={'border-b last:border-b-0 hover:bg-muted/50 ' + (r.driftDetected ? 'cursor-pointer' : '')}
                    onClick={() => r.driftDetected && setExpanded(isExpanded ? null : r.id)}
                  >
                    <Td className="text-xs text-muted-foreground">{new Date(r.ranAt).toLocaleString()}</Td>
                    <Td className="font-mono text-xs text-foreground">{r.tenantSlug}</Td>
                    <Td className="text-xs tabular-nums">
                      {r.serversInDoable} / {r.serversInHetzner}
                    </Td>
                    <Td>
                      {r.driftDetected ? (
                        <Badge variant="warning">
                          {Array.isArray(r.driftDetails)
                            ? `${r.driftDetails.length} drift${r.driftDetails.length === 1 ? '' : 's'}`
                            : 'drift'}
                        </Badge>
                      ) : (
                        <Badge variant="success">clean</Badge>
                      )}
                    </Td>
                    <Td right className="text-xs text-muted-foreground">
                      {r.durationMs !== null ? `${r.durationMs}ms` : '—'}
                    </Td>
                  </tr>
                  {isExpanded && r.driftDetails ? (
                    <tr className="border-b bg-muted/30">
                      <td colSpan={5} className="px-3 py-3">
                        <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-muted-foreground">
                          {JSON.stringify(r.driftDetails, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </Table>
      )}
    </>
  )
}

function FleetSummary({
  tenants,
  members,
  servers,
  monthlyEur,
  anthropic30d,
}: {
  tenants: number
  members: number
  servers: number
  monthlyEur: number
  anthropic30d: number
}) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <Stat label="Tenants" value={String(tenants)} />
      <Stat label="Members" value={String(members)} />
      <Stat label="Servers" value={String(servers)} />
      <Stat label="Hetzner €/mo" value={`€${monthlyEur.toFixed(2)}`} />
      <Stat label="Anthropic 30d" value={`$${anthropic30d.toFixed(2)}`} />
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card size="sm">
      <CardContent>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function Loading() {
  return (
    <div className="space-y-3">
      <div className="dwlt-skeleton h-10" />
      <div className="dwlt-skeleton h-10" />
      <div className="dwlt-skeleton h-10" />
    </div>
  )
}

function ErrorBox({ error }: { error: ApiError }) {
  if (error.status === 403) {
    return (
      <StatusBox tone="warning">
        You're not on the platform-admin allowlist. If you should be, ask
        Karim to add your email to <code className="font-mono">PLATFORM_ADMIN_EMAILS</code>.
      </StatusBox>
    )
  }
  return <StatusBox tone="destructive">{error.title ?? 'Failed to load'}</StatusBox>
}

type FetchState<T> =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError }
  | { kind: 'value'; value: T }

function useFetched<T>(fetcher: () => Promise<T>): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({ kind: 'loading' })
  useEffect(() => {
    let cancelled = false
    fetcher()
      .then((value) => {
        if (!cancelled) setState({ kind: 'value', value })
      })
      .catch((error: ApiError) => {
        if (!cancelled) setState({ kind: 'error', error })
      })
    return () => {
      cancelled = true
    }
    // fetcher captures accessToken; tab change re-mounts the component
    // anyway so this is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return state
}
